import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase-config";
import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";

function UserProfile() {
    const [profile, setProfile] = useState({
        firstName: "",
        lastName: "",
        phone: "",
        practiceName: "",
        email: "",
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState("");
    const [isPaying, setIsPaying] = useState(false);
    const [stripeCustomerId, setStripeCustomerId] = useState("");
    const [liveSubscription, setLiveSubscription] = useState(null);
    const [subscriptionLoading, setSubscriptionLoading] = useState(false);
    const [billingStatus, setBillingStatus] = useState("");
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [phoneError, setPhoneError] = useState("");

    useEffect(() => {
        const user = auth.currentUser;
        if (!user) {
            setLoading(false);
            return;
        }

        const ref = doc(db, "user_profiles", user.uid);
        const unsubscribe = onSnapshot(ref, (snap) => {
            const data = snap.exists() ? snap.data() : {};
            const savedProfile = data?.profile || {};
            setProfile({
                firstName: savedProfile.firstName || "",
                lastName: savedProfile.lastName || "",
                phone: savedProfile.phone || "",
                practiceName: savedProfile.practiceName || "",
                email: savedProfile.email || user.email || "",
            });
            setIsPaying(Boolean(data?.is_paying));
            setStripeCustomerId(data?.stripeCustomerId || "");
            setLoading(false);
        }, (error) => {
            console.error("Failed to load profile", error);
            setStatus("Could not load profile data.");
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const handleChange = (field) => (event) => {
        const value = event.target.value;
        setProfile((prev) => ({ ...prev, [field]: value }));
        if (field === "phone") {
            setPhoneError("");
        }
    };

    const saveProfile = async () => {
        const user = auth.currentUser;
        if (!user) return;

        const phoneValue = profile.phone.trim();
        const phonePattern = /^\d{3}-\d{3}-\d{4}$/;
        if (phoneValue && !phonePattern.test(phoneValue)) {
            setPhoneError("Phone must be in XXX-XXX-XXXX format.");
            setStatus("Please fix the phone number format.");
            return;
        }

        setSaving(true);
        setStatus("");
        try {
            const ref = doc(db, "user_profiles", user.uid);
            await setDoc(
                ref,
                {
                    profile: {
                        firstName: profile.firstName.trim(),
                        lastName: profile.lastName.trim(),
                        phone: profile.phone.trim(),
                        practiceName: profile.practiceName.trim(),
                        email: user.email || profile.email,
                    },
                    profileUpdatedAt: serverTimestamp(),
                },
                { merge: true }
            );
            setStatus("Profile saved.");
        } catch (error) {
            console.error("Failed to save profile", error);
            setStatus("Failed to save profile.");
        } finally {
            setSaving(false);
        }
    };

    const fetchSubscriptionStatus = async () => {
        const user = auth.currentUser;
        if (!user) return;

        const statusUrl = process.env.REACT_APP_GET_SUBSCRIPTION_STATUS_URL;
        if (!statusUrl) return;

        setSubscriptionLoading(true);
        try {
            const idToken = await user.getIdToken();
            const resp = await fetch(statusUrl, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${idToken}`,
                },
            });

            const data = await resp.json();
            if (!resp.ok) {
                throw new Error(data?.error || "Failed to fetch subscription status");
            }

            setLiveSubscription({
                hasActiveSubscription: Boolean(data?.hasActiveSubscription),
                status: data?.status || "inactive",
                cancelAtPeriodEnd: Boolean(data?.cancelAtPeriodEnd),
                currentPeriodEnd: data?.currentPeriodEnd || null,
            });
        } catch (error) {
            console.error("Failed to fetch subscription status", error);
            setLiveSubscription(null);
        } finally {
            setSubscriptionLoading(false);
        }
    };

    useEffect(() => {
        if (!stripeCustomerId) {
            setLiveSubscription(null);
            return;
        }
        fetchSubscriptionStatus();
    }, [stripeCustomerId]);

    const startCheckout = async () => {
        const user = auth.currentUser;
        if (!user) return;

        setCheckoutLoading(true);
        setBillingStatus("");
        try {
            const checkoutUrl = process.env.REACT_APP_CREATE_CHECKOUT_SESSION_URL;
            if (!checkoutUrl) {
                throw new Error("REACT_APP_CREATE_CHECKOUT_SESSION_URL is not set");
            }

            const idToken = await user.getIdToken();
            const resp = await fetch(checkoutUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${idToken}`,
                },
            });

            const data = await resp.json();
            if (!resp.ok) {
                throw new Error(data?.error || "Failed to start checkout");
            }

            if (data?.url) {
                window.location.assign(data.url);
                return;
            }

            throw new Error("Checkout session URL missing in response");
        } catch (error) {
            console.error("Checkout failed", error);
            setBillingStatus(error.message || "Checkout failed");
        } finally {
            setCheckoutLoading(false);
        }
    };

    const openBillingPortal = async () => {
        const user = auth.currentUser;
        if (!user) return;

        setCheckoutLoading(true);
        setBillingStatus("");
        try {
            const portalUrl = process.env.REACT_APP_CREATE_PORTAL_SESSION_URL;
            if (!portalUrl) {
                throw new Error("REACT_APP_CREATE_PORTAL_SESSION_URL is not set");
            }

            const idToken = await user.getIdToken();
            const resp = await fetch(portalUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${idToken}`,
                },
            });

            const data = await resp.json();
            if (!resp.ok) {
                throw new Error(data?.error || "Failed to open billing portal");
            }

            if (data?.url) {
                window.location.assign(data.url);
                return;
            }

            throw new Error("Portal session URL missing in response");
        } catch (error) {
            console.error("Billing portal failed", error);
            setBillingStatus(error.message || "Billing portal failed");
        } finally {
            setCheckoutLoading(false);
        }
    };

    if (loading) {
        return <div className="page">Loading profile...</div>;
    }

    return (
        <div className="page">
            <h1>User Profile</h1>

            <div className="profile-grid">
                <div className="card">
                    <h3 className="section-title">Profile Details</h3>
                    <div className="profile-form">
                        <label>
                            First Name
                            <input
                                type="text"
                                value={profile.firstName}
                                onChange={handleChange("firstName")}
                                placeholder="Jane"
                            />
                        </label>
                        <label>
                            Last Name
                            <input
                                type="text"
                                value={profile.lastName}
                                onChange={handleChange("lastName")}
                                placeholder="Smith"
                            />
                        </label>
                        <label>
                            Phone
                            <input
                                type="tel"
                                value={profile.phone}
                                onChange={handleChange("phone")}
                                placeholder="(555) 123-4567"
                                pattern="\d{3}-\d{3}-\d{4}"
                                inputMode="numeric"
                            />
                            {phoneError && (
                                <span className="profile-status" style={{ color: "#B91C1C" }}>
                                    {phoneError}
                                </span>
                            )}
                        </label>
                        <label>
                            Practice Name
                            <input
                                type="text"
                                value={profile.practiceName}
                                onChange={handleChange("practiceName")}
                                placeholder="Sunrise Dental"
                            />
                        </label>
                        <label>
                            Email
                            <input type="email" value={profile.email} disabled />
                        </label>
                        <div className="profile-actions">
                            <button onClick={saveProfile} disabled={saving}>
                                {saving ? "Saving..." : "Save Profile"}
                            </button>
                            {status && <span className="profile-status">{status}</span>}
                        </div>
                    </div>
                </div>

                <div className="card">
                    <h3 className="section-title">Billing (Stripe)</h3>
                    <div className="stripe-placeholder">
                        <p>
                            Status: {subscriptionLoading
                                ? "Checking..."
                                : (liveSubscription
                                    ? (liveSubscription.hasActiveSubscription
                                        ? "Active"
                                        : "Inactive")
                                    : (isPaying ? "Active" : "Inactive"))}
                        </p>
                        {liveSubscription?.cancelAtPeriodEnd && liveSubscription?.currentPeriodEnd && (
                            <p>
                                Cancellation scheduled. Access ends on {new Date(liveSubscription.currentPeriodEnd * 1000).toLocaleDateString()}.
                            </p>
                        )}
                        <p>Subscriptions are managed through Stripe Checkout.</p>
                        <div className="row">
                            <button onClick={startCheckout} disabled={checkoutLoading}>
                                {checkoutLoading ? "Redirecting..." : "Start Subscription"}
                            </button>
                            <button
                                onClick={openBillingPortal}
                                disabled={checkoutLoading || !stripeCustomerId}
                            >
                                Manage Billing
                            </button>
                        </div>
                        {billingStatus && (
                            <span className="profile-status">{billingStatus}</span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UserProfile;
